import React, { useMemo } from 'react';
import {
	type Command,
	type CommandAction,
	CommandActionType,
	type CommandInternal,
	type CommandSection,
	type CommandSections,
} from '../../../../../../common/types/commands/index.tsx';
import { useCommandPalette } from '../../../../../command-palette/index.tsx';
import { SEARCH_MAX_DEPTH } from '../../../constants.tsx';

const getSearchableChildrenCommands = (action?: CommandAction): CommandInternal[] => {
	if (action?.type === CommandActionType.COMMAND_LIST && action.commands !== undefined) {
		return action.commands;
	}

	return [];
};

export const useSearchableCommands = (commands: Command[]): CommandInternal[] => {
	const [{ sections }] = useCommandPalette();
	return useMemo(() => prepareSearchCommands(commands, sections), [commands, sections]);
};

const getSectionName = (
	command: CommandInternal,
	sectionMap: Map<string, CommandSection>,
): string => {
	const sectionId = command.section ?? '';
	const section = sectionMap.get(sectionId);
	if (!section) return '';

	if (section.name) return section.name;

	const textContent = (elem: React.ReactElement | string): string => {
		if (!elem) return '';
		if (typeof elem === 'string') return elem;
		const children = elem.props && elem.props.children;
		return children instanceof Array ? children.map(textContent).join('') : textContent(children);
	};
	return textContent(section.renderName?.() ?? '');
};

export const prepareSearchCommands = (
	commands: Command[],
	sections: CommandSections[],
): CommandInternal[] => {
	const sectionMap = new Map(sections.flatMap((section) => Object.entries(section)));
	const findCommands = (currentCommands: Command[], depth = 1): CommandInternal[] => {
		const allCommands: CommandInternal[] = [];
		const commandsToPrepare = currentCommands;
		commandsToPrepare.forEach((command) => {
			allCommands.push({
				...command,
				searchKeywordsArray: command.keywords?.split(' ') ?? [],
				sectionName: getSectionName(command, sectionMap),
			});
			const searchableChildren: CommandInternal[] = [];
			const searchablePrimaryChildren = getSearchableChildrenCommands(command.primaryAction);
			const searchableSecondaryChildren = getSearchableChildrenCommands(command.secondaryAction);
			if (depth < SEARCH_MAX_DEPTH && searchablePrimaryChildren.length) {
				searchableChildren.push(...findCommands(searchablePrimaryChildren, depth + 1));
			}
			if (depth < SEARCH_MAX_DEPTH && searchableSecondaryChildren.length) {
				searchableChildren.push(...findCommands(searchableSecondaryChildren, depth + 1));
			}
			searchableChildren.forEach((childCommand) => {
				const breadcrumb: CommandInternal['breadcrumb'] = [
					command?.components?.Name || (() => <>{command.name}</>),
				];

				if (childCommand.breadcrumb?.length) {
					breadcrumb.push(...childCommand.breadcrumb);
				} else {
					breadcrumb.push(childCommand?.components?.Name || (() => <>{childCommand.name}</>));
				}

				const parentCommands = [command];

				if (childCommand.parentCommands?.length) {
					parentCommands.push(...childCommand.parentCommands);
				}

				allCommands.push({
					...childCommand,
					searchKeywordsArray: childCommand.keywords?.split(' ') ?? [],
					section: command.section,
					sectionName: getSectionName(command, sectionMap),
					parentCommands,
					breadcrumb,
				});
			});
		});

		return allCommands;
	};

	return findCommands(commands);
};
